// Face sign-in: the station webcam (one shared stream per page).
//
// acquireCamera() opens the webcam (or reuses the open stream) and counts the user; every
// acquire is paired with releaseCamera(). The stream stays warm for IDLE_MS after the last
// release, so a run of PIN prompts doesn't re-open it each time, then every track is stopped
// (the webcam light goes off when JobQ isn't looking).
//
// Anything that stops the camera (denied, busy, missing, not https) makes acquireCamera()
// resolve null and lastCameraError() say why: the PIN prompt then just shows the keypad.

export const IDLE_MS = 30 * 1000;
export const SETTLE_MS = 400; // after a cold open, let auto-exposure settle before the first picture
export const FRAME_WAIT_MS = 3000;
const MAX_WIDTH = 960;
const QUALITY = 0.85;

let stream = null;
let opening = null; // Promise<MediaStream|null>
let users = 0;
let idleTimer = null;
let settled = false;
let grabVideo = null; // hidden <video> used to read frames when ImageCapture isn't there
let lastError = null;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** True when this browser can open a camera at all (https + getUserMedia). */
export function cameraSupported() {
  try {
    return Boolean(
      typeof window !== 'undefined' && window.isSecureContext
      && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function'
    );
  } catch (e) {
    return false;
  }
}

/** Why the camera couldn't open last time, in a short sentence (null when it opened). */
export function lastCameraError() {
  return lastError;
}

export function cameraErrorText(err) {
  const name = (err && err.name) || '';
  const msg = (err && err.message) || String(err || '');
  if (/insecure|secure context/i.test(msg)) return 'The camera only works on the secure (https) JobQ site.';
  if (/NotAllowed|Permission|denied|Security/i.test(name + msg)) return 'JobQ isn\'t allowed to use the camera on this computer.';
  if (/NotFound|Overconstrained|DevicesNotFound/i.test(name + msg)) return 'No camera was found on this computer.';
  if (/NotReadable|TrackStart|in use|AbortError/i.test(name + msg)) return 'The camera is busy in another app.';
  return `The camera could not start (${msg || name || 'unknown error'}).`;
}

function live(s) {
  return Boolean(s && s.getVideoTracks && s.getVideoTracks().some((t) => t.readyState === 'live'));
}

function clearIdle() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function scheduleIdle() {
  clearIdle();
  if (users > 0 || !stream) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (users === 0) stopCamera();
  }, IDLE_MS);
}

/** Stop every track now (Stop using face sign-in; the idle timer). */
export function stopCamera() {
  clearIdle();
  const s = stream;
  stream = null;
  settled = false;
  if (grabVideo) {
    try { grabVideo.srcObject = null; grabVideo.remove(); } catch (e) { /* gone */ }
    grabVideo = null;
  }
  if (s) {
    try { s.getTracks().forEach((t) => t.stop()); } catch (e) { /* already stopped */ }
  }
}

async function open() {
  if (!cameraSupported()) {
    lastError = cameraErrorText(new Error('insecure context: no camera access'));
    return null;
  }
  try {
    const s = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      audio: false,
    });
    lastError = null;
    stream = s;
    settled = false;
    // A camera unplugged mid-use ends its track: forget the stream so the next acquire re-opens.
    s.getVideoTracks().forEach((t) => {
      try { t.addEventListener('ended', () => { if (stream === s) stopCamera(); }); } catch (e) { /* old browser */ }
    });
    return s;
  } catch (err) {
    lastError = cameraErrorText(err);
    return null;
  }
}

/** Open (or reuse) the webcam and count this user. Resolves the MediaStream, or null (keypad). */
export async function acquireCamera() {
  users += 1;
  clearIdle();
  if (live(stream)) return stream;
  if (stream) stopCamera();
  if (!opening) {
    opening = open().finally(() => {
      opening = null;
      // Everyone let go while it was opening (the prompt closed): start the idle countdown.
      if (users === 0) scheduleIdle();
    });
  }
  return opening;
}

/** Pair of acquireCamera(); after the last one the stream stays warm IDLE_MS, then stops. */
export function releaseCamera() {
  users = Math.max(0, users - 1);
  if (users === 0) scheduleIdle();
}

/** The open stream, for a preview <video> (null when closed). */
export function getLiveStream() {
  return live(stream) ? stream : null;
}

function hiddenVideo(s) {
  if (grabVideo && grabVideo.srcObject === s) return grabVideo;
  if (grabVideo) {
    try { grabVideo.srcObject = null; grabVideo.remove(); } catch (e) { /* gone */ }
  }
  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;
  v.setAttribute('playsinline', '');
  v.setAttribute('aria-hidden', 'true');
  // On screen but invisible: Chrome may pause video it considers hidden.
  v.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0.01;pointer-events:none;z-index:-1;';
  v.srcObject = s;
  document.body.appendChild(v);
  try {
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) { /* jsdom */ }
  grabVideo = v;
  return v;
}

async function waitForFrames(v, timeoutMs) {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    if (v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) return true;
    // eslint-disable-next-line no-await-in-loop
    await sleep(50);
  }
  return false;
}

function toJpeg(source, w, h) {
  const width = Math.min(MAX_WIDTH, w);
  const height = Math.round((h * width) / w);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  const url = canvas.toDataURL('image/jpeg', QUALITY);
  const comma = url.indexOf(',');
  return comma >= 0 && url.startsWith('data:image/jpeg') ? url.slice(comma + 1) : null;
}

/**
 * One picture from the open stream: base64 JPEG (no data: prefix), at most 960 px wide.
 * Waits for the camera's first frames, plus SETTLE_MS after a cold open. Null when the camera
 * isn't open or gives no frame in time.
 */
export async function captureJpeg() {
  const s = getLiveStream();
  if (!s) return null;
  try {
    const track = s.getVideoTracks()[0];
    if (typeof window.ImageCapture === 'function' && track) {
      const ic = new window.ImageCapture(track);
      if (!settled) {
        const first = await ic.grabFrame(); // resolves once the camera delivers frames
        if (first && first.close) first.close();
        await sleep(SETTLE_MS); // the first frames are often dark: give exposure a moment
      }
      const bitmap = await ic.grabFrame();
      const out = toJpeg(bitmap, bitmap.width, bitmap.height);
      if (bitmap.close) bitmap.close();
      if (out) {
        settled = true;
        return out;
      }
    }
  } catch (e) { /* fall through to the video element */ }
  const v = hiddenVideo(s);
  if (!(await waitForFrames(v, FRAME_WAIT_MS))) return null;
  if (!settled) {
    settled = true;
    await sleep(SETTLE_MS); // the first frame is often dark: give exposure a moment
  }
  if (getLiveStream() !== s) return null;
  try {
    return toJpeg(v, v.videoWidth, v.videoHeight);
  } catch (e) {
    return null;
  }
}
